import { useCallback } from "react";
import type { ApiClient, PipelineRun } from "./api";
import { apiProblem, useLoaded } from "./useLoaded";

export interface PipelineRunState {
  /** The newest run, `null` when there is none, `undefined` until the first answer. */
  run: PipelineRun | null | undefined;
  problem: string | null;
  /** Adopt a run the caller just created, so the screen does not wait for a poll. */
  adopt: (run: PipelineRun) => void;
  reload: () => void;
}

const pollWhileInFlight = (run: PipelineRun | null | undefined) =>
  run?.status === "queued" || run?.status === "processing" ? 2000 : null;

const problem = apiProblem("The pipeline could not be read.");

/**
 * The newest run over one source, kept current while anything is still moving.
 *
 * One hook for every screen that needs the run, because the recording detail decides from
 * it whether to show the pipeline or the transcript while the panel inside it draws the
 * steps. Two components each polling the same endpoint would be two timers, twice the
 * requests, and — for a second or two at a time — two different opinions about the same run.
 *
 * The cadence matches the recordings list, and stops the moment the run settles: a
 * finished recording costs nothing to leave open.
 */
export function usePipelineRun(api: ApiClient, sourceId: string | undefined): PipelineRunState {
  const read = useCallback((client: ApiClient) => client.getPipeline(sourceId!), [sourceId]);

  const { value, problem: failure, set, refresh } = useLoaded<PipelineRun | null | undefined>(
    api,
    read,
    undefined,
    problem,
    { poll: pollWhileInFlight, enabled: sourceId !== undefined },
  );

  return { run: value, problem: failure, adopt: set, reload: refresh };
}
